/**
 * Helpers for rendering `fields_changed` entries from `*_edit_histories`.
 *
 * Background: Rails ActiveRecord `self.changes` tracks dirtiness at column
 * level. For `jsonb` columns like `Product#custom_attributes`, no matter how
 * many keys inside the hash were modified, the backend will only emit one
 * entry — `"custom_attributes" => [oldHash, newHash]`. The frontend is
 * responsible for unpacking the per-key diff for display; otherwise users
 * see `[object Object] → [object Object]`.
 */

export interface PlainObject {
  [key: string]: unknown;
}

export interface HashDiffEntry {
  /** Raw key inside the jsonb (e.g. `"stacking_method"`). */
  key: string;
  /** Old value at `key`, or `undefined` if the key was added. */
  oldValue: unknown;
  /** New value at `key`, or `undefined` if the key was removed. */
  newValue: unknown;
}

/**
 * True when `value` is a plain JS object (not array, not null, not Date/etc.).
 *
 * We restrict to `Object.prototype` so we don't accidentally treat class
 * instances or arrays as hash-diff candidates.
 */
export function isPlainObject(value: unknown): value is PlainObject {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

/**
 * Returns the per-key diff between two plain objects, **only** including keys
 * whose values changed. Stable order: keys are sorted alphabetically so the
 * UI doesn't reshuffle on every render.
 *
 * Equality is shallow JSON equality — sufficient for the jsonb scalar values
 * users actually store in `custom_attributes` (strings, numbers, booleans).
 */
export function diffPlainObjects(
  oldObj: PlainObject | null | undefined,
  newObj: PlainObject | null | undefined,
): HashDiffEntry[] {
  const left = oldObj ?? {};
  const right = newObj ?? {};
  const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).sort();

  const entries: HashDiffEntry[] = [];
  for (const key of keys) {
    const oldValue = left[key];
    const newValue = right[key];
    if (jsonEqual(oldValue, newValue)) continue;
    entries.push({ key, oldValue, newValue });
  }
  return entries;
}

function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Treat `undefined` and missing key the same as `null` for diff purposes —
  // ActiveRecord serializes missing keys / nil identically through jsonb.
  if (a == null && b == null) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
